/**
 * Session storage utilities for StructureNote
 * Handles subscription state persistence across page refreshes
 * Eliminates duplicate sessionStorage operations from original lines 245-251, 257-263
 */

import { SubscriptionSessionState } from "../StructureNote.types";

/**
 * Session storage keys - single source of truth
 */
export const SESSION_KEYS = {
  CATEGORY: "category",
  REAL_URL: "realUrl",
  DOCUMENT_ID: "documentId",
  COMPLETED_AT: "completedAt",
  INVESTOR_SUBSCRIBE_AMOUNT: "investorSubscribeAmount",
  SUBSCRIBE_DATA: "subscribeData",
  REGTANK_SESSION_ID: "regtankSessionId",
  DOC_SIGNATURE_FETCHED: "docSignatureFetched",
} as const;

/**
 * Load subscription state from session storage
 * Original logic from lines 153-176
 *
 * @returns Subscription state if all required fields present, null otherwise
 */
export const loadSubscriptionFromSession =
  (): SubscriptionSessionState | null => {
    const category = sessionStorage.getItem(SESSION_KEYS.CATEGORY);
    const realUrl = sessionStorage.getItem(SESSION_KEYS.REAL_URL);
    const documentId = sessionStorage.getItem(SESSION_KEYS.DOCUMENT_ID);
    const completedAt = sessionStorage.getItem(SESSION_KEYS.COMPLETED_AT);
    const investorSubscribeAmount = sessionStorage.getItem(
      SESSION_KEYS.INVESTOR_SUBSCRIBE_AMOUNT
    );
    const subscribeData = sessionStorage.getItem(SESSION_KEYS.SUBSCRIBE_DATA);

    // Check all required fields are present (original logic from lines 161-168)
    if (
      category === "subscribe" &&
      realUrl !== null &&
      documentId !== null &&
      investorSubscribeAmount !== null &&
      subscribeData !== null
    ) {
      return {
        category: "subscribe",
        realUrl,
        documentId: parseInt(documentId),
        completedAt: "",
        investorSubscribeAmount,
        selectedData: JSON.parse(subscribeData),
      };
    }

    return null;
  };

/**
 * Save subscription state to session storage
 * Original logic from lines 418-431
 *
 * @param state - Subscription state to persist
 */
export const saveSubscriptionToSession = (
  state: Partial<SubscriptionSessionState>
): void => {
  if (state.category) {
    sessionStorage.setItem(SESSION_KEYS.CATEGORY, state.category);
  }
  if (state.realUrl) {
    sessionStorage.setItem(SESSION_KEYS.REAL_URL, state.realUrl);
  }
  if (state.documentId !== undefined) {
    sessionStorage.setItem(
      SESSION_KEYS.DOCUMENT_ID,
      state.documentId.toString()
    );
  }
  if (state.completedAt) {
    sessionStorage.setItem(SESSION_KEYS.COMPLETED_AT, state.completedAt);
  }
  if (state.investorSubscribeAmount) {
    sessionStorage.setItem(
      SESSION_KEYS.INVESTOR_SUBSCRIBE_AMOUNT,
      state.investorSubscribeAmount
    );
  }
  if (state.selectedData) {
    sessionStorage.setItem(
      SESSION_KEYS.SUBSCRIBE_DATA,
      JSON.stringify(state.selectedData)
    );
  }
  if (state.sessionId) {
    sessionStorage.setItem(SESSION_KEYS.REGTANK_SESSION_ID, state.sessionId);
  }
};

/**
 * Clear all subscription-related session storage
 * Consolidates duplicate code from lines 245-251, 257-263, 465
 *
 * Note: When user clicks "Continue" to complete the flow and return to subscribed notes,
 * this clears all session state including the docSignatureFetched flag, allowing the
 * user to initiate a new subscription if needed
 */
export const clearSubscriptionSession = (): void => {
  Object.values(SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Get Regtank session ID from session storage
 *
 * @returns Session ID or null if not found
 */
export const getRegtankSessionId = (): string | null => {
  return sessionStorage.getItem(SESSION_KEYS.REGTANK_SESSION_ID);
};
